import { Link } from "react-router-dom";
import {
  Award, BookOpen, Clock, Download, GraduationCap, PlayCircle, TrendingUp,
} from "lucide-react";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useMyFormations, useMyCertificates } from "@/hooks/useFormations";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

function Ring({ value }) {
  return (
    <div
      className="relative flex h-12 w-12 items-center justify-center rounded-full"
      style={{ background: `conic-gradient(#0052FF ${value * 3.6}deg, rgba(255,255,255,0.25) 0deg)` }}
    >
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-black/70 text-[11px] font-bold text-white">
        {value}%
      </div>
    </div>
  );
}

const ACCESS_BADGE = {
  active: { label: "En cours", cls: "bg-brand-light text-brand" },
  completed: { label: "Terminée", cls: "bg-emerald-50 text-emerald-700" },
  pending: { label: "En attente de paiement", cls: "bg-amber-50 text-amber-700" },
  suspended: { label: "Suspendu", cls: "bg-slate-100 text-slate-600" },
  expired: { label: "Expiré", cls: "bg-rose-50 text-rose-700" },
  revoked: { label: "Révoqué", cls: "bg-rose-50 text-rose-700" },
};

function StatCard({ icon: Icon, tone, value, label, sub }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-line bg-white p-4">
      <span className={`flex h-12 w-12 items-center justify-center rounded-xl ${tone}`}><Icon className="h-6 w-6" /></span>
      <div>
        <p className="font-display text-2xl font-extrabold text-ink">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
        {sub && <p className="text-[11px] text-slate-400">{sub}</p>}
      </div>
    </div>
  );
}

export default function AccountFormations() {
  const { data, isLoading } = useMyFormations();
  const { data: certificates = [] } = useMyCertificates();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") === "certificats" ? "certificats" : "formations";
  const [celebration, setCelebration] = useState(null);
  const celebrated = useRef(false);

  // Animation de félicitations quand un certificat vient d'être délivré
  useEffect(() => {
    if (celebrated.current || !certificates.length) return;
    if (searchParams.get("tab") !== "certificats") return;
    const seen = JSON.parse(localStorage.getItem("seen_certificates") || "[]");
    const fresh = certificates.find((c) => !seen.includes(c.id));
    if (fresh) {
      celebrated.current = true;
      setCelebration(fresh);
      localStorage.setItem("seen_certificates", JSON.stringify([...seen, ...certificates.map((c) => c.id)]));
    }
  }, [certificates, searchParams]);

  const items = data?.items || [];
  const stats = data?.stats || { count: 0, avg_progress: 0, certificates: 0, completed: 0 };

  return (
    <div className="space-y-6" data-testid="account-formations">
      {celebration && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/60 p-4 backdrop-blur-sm" data-testid="certificate-celebration">
          <div className="w-full max-w-md animate-in zoom-in-95 rounded-3xl bg-white p-8 text-center shadow-2xl">
            <div className="mx-auto flex h-20 w-20 animate-bounce items-center justify-center rounded-full bg-amber-100">
              <span className="text-4xl">🎓</span>
            </div>
            <p className="mt-4 font-display text-2xl font-extrabold text-ink">Félicitations !</p>
            <p className="mt-2 text-sm text-slate-600">
              Vous avez obtenu le certificat <span className="font-bold text-brand">« {celebration.formation_title} »</span>. Bravo pour votre persévérance !
            </p>
            <Link to={`/certificat/${celebration.certificate_number}`}
              data-testid="celebration-download"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-amber-500 py-3.5 text-sm font-bold text-white hover:bg-amber-600">
              <Award className="h-4 w-4" /> Voir mon certificat
            </Link>
            <button onClick={() => setCelebration(null)} className="mt-3 text-xs font-bold text-slate-400 hover:text-ink" data-testid="celebration-close">Fermer</button>
          </div>
        </div>
      )}
      <div>
        <h1 className="font-display text-2xl font-extrabold text-ink">🎓 Mes formations</h1>
        <p className="mt-1 text-sm text-slate-500">Continuez votre apprentissage là où vous vous êtes arrêté.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard icon={BookOpen} tone="bg-brand-light text-brand" value={stats.count} label="Formations" sub="Accès actif" />
        <StatCard icon={TrendingUp} tone="bg-emerald-50 text-emerald-600" value={`${stats.avg_progress}%`} label="Progression moyenne" />
        <StatCard icon={GraduationCap} tone="bg-violet-50 text-violet-600" value={stats.completed} label="Formations terminées" />
        <StatCard icon={Award} tone="bg-amber-50 text-amber-600" value={stats.certificates} label="Certificats obtenus" />
      </div>

      <Tabs defaultValue={defaultTab}>
        <TabsList className="bg-transparent p-0">
          <TabsTrigger value="formations" className="rounded-full data-[state=active]:bg-brand data-[state=active]:text-white">Toutes mes formations</TabsTrigger>
          <TabsTrigger value="certificats" data-testid="tab-certificats" className="rounded-full data-[state=active]:bg-brand data-[state=active]:text-white">Mes certificats</TabsTrigger>
        </TabsList>

        <TabsContent value="formations" className="mt-5">
          {isLoading ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-line bg-white py-16 text-center">
              <GraduationCap className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-3 font-display text-lg font-bold text-ink">Aucune formation pour l'instant</p>
              <p className="mt-1 text-sm text-slate-500">Découvrez notre catalogue de formations en ligne.</p>
              <Link to="/formations" className="mt-4 inline-block rounded-full bg-brand px-6 py-2.5 text-sm font-bold text-white">Voir les formations</Link>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {items.map((f) => {
                const badge = ACCESS_BADGE[f.access_status] || ACCESS_BADGE.active;
                const locked = f.access_status === "pending";
                return (
                  <div key={f.id} data-testid={`my-formation-${f.slug}`} className="flex flex-col overflow-hidden rounded-2xl border border-line bg-white">
                    <div className="relative">
                      <img src={f.image_url} alt="" className="aspect-[16/9] w-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      <div className="absolute right-3 top-3"><Ring value={f.progress} /></div>
                    </div>
                    <div className="flex flex-1 flex-col p-4">
                      <div className="flex items-center gap-2">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${badge.cls}`}>{badge.label}</span>
                        {f.has_certificate && <span className="flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-600"><Award className="h-3 w-3" /> Certifié</span>}
                      </div>
                      <h3 className="mt-2 line-clamp-1 font-display text-base font-bold text-ink">{f.title}</h3>
                      <p className="mt-1 flex flex-wrap items-center gap-x-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><BookOpen className="h-3.5 w-3.5" /> {f.lessons_count} cours</span>
                        {f.duration && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {f.duration}</span>}
                      </p>
                      {f.last_lesson && <p className="mt-1.5 truncate text-xs text-slate-400">Dernier cours : {f.last_lesson}</p>}
                      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full rounded-full bg-brand" style={{ width: `${f.progress}%` }} />
                      </div>
                      <div className="mt-auto pt-3">
                        {locked ? (
                          <div className="rounded-full bg-amber-50 px-4 py-2.5 text-center text-xs font-bold text-amber-700">Paiement en attente de confirmation</div>
                        ) : (
                          <Link to={`/mes-formations/${f.id}`} data-testid={`continue-${f.slug}`} className="flex w-full items-center justify-center gap-1.5 rounded-full bg-brand px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-dark">
                            <PlayCircle className="h-4 w-4" /> {f.progress > 0 ? "Continuer la formation" : "Commencer la formation"}
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="certificats" className="mt-5">
          {certificates.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-line bg-white py-16 text-center">
              <Award className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-3 font-display text-lg font-bold text-ink">Aucun certificat pour l'instant</p>
              <p className="mt-1 text-sm text-slate-500">Terminez une formation pour obtenir votre certificat.</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {certificates.map((c) => (
                <div key={c.id} data-testid={`certificate-${c.id}`} className="flex items-center gap-4 rounded-2xl border border-line bg-white p-4">
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50 text-amber-600"><Award className="h-7 w-7" /></span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-display text-base font-bold text-ink">{c.formation_title}</p>
                    <p className="text-xs text-slate-500">N° {c.certificate_number}</p>
                    <p className="text-xs text-slate-400">Délivré le {formatDate(c.issued_at)}</p>
                  </div>
                  <Link to={`/certificat/${c.certificate_number}`} className="flex items-center gap-1.5 rounded-full bg-amber-500 px-4 py-2.5 text-xs font-bold text-white hover:bg-amber-600">
                    <Award className="h-4 w-4" /> Voir
                  </Link>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
